import React, { useState } from 'react'
import { cn } from '../../lib/utils'

export interface PresenterNotesProps {
  notes?: string
  slideTitle?: string
  slideDuration?: number
  className?: string
}

/**
 * PresenterNotes Component
 *
 * Displays presenter notes for the current slide.
 * Includes slide timing information and expandable notes area.
 */
export const PresenterNotes: React.FC<PresenterNotesProps> = ({
  notes,
  slideTitle,
  slideDuration,
  className,
}) => {
  const [isExpanded, setIsExpanded] = useState(true)

  // If no notes, show minimal info or empty state
  const hasNotes = notes && notes.trim().length > 0

  return (
    <div
      className={cn(
        'flex flex-col bg-card rounded-lg shadow-lg',
        'border border-border',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-border">
        <div className="flex items-center gap-2">
          <svg
            className="w-4 h-4 text-muted-foreground"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <span className="text-sm font-medium text-foreground">
            Notes
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Duration indicator */}
          {slideDuration && (
            <span className="text-xs text-muted-foreground">
              {slideDuration}s
            </span>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1 rounded hover:bg-muted transition-colors"
            aria-label={isExpanded ? 'Collapse notes' : 'Expand notes'}
          >
            <svg
              className={cn(
                'w-4 h-4 text-muted-foreground transition-transform',
                isExpanded && 'rotate-180'
              )}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Notes Content */}
      {isExpanded && (
        <div className="p-4">
          {hasNotes ? (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              {notes.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-2 last:mb-0">
                  {paragraph}
                </p>
              ))}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground italic">
              No notes for this slide.
            </div>
          )}
        </div>
      )}

      {/* Slide info footer */}
      {slideTitle && (
        <div className="px-4 py-2 bg-muted rounded-b-lg border-t border-border">
          <div className="text-xs text-muted-foreground truncate">
            Current: {slideTitle}
          </div>
        </div>
      )}
    </div>
  )
}

export default PresenterNotes
